<template>
  <section class="system-stats">
    <div class="system-heading">
      <h3 class="system-title">system</h3>
      <div class="range-row" role="group" aria-label="Chart range">
        <button
          v-for="item in rangeChoices"
          :key="item.id"
          type="button"
          class="range-button"
          :class="{ 'range-button-active': range === item.id }"
          @click="setRange(item.id)"
        >{{ item.label }}</button>
      </div>
    </div>
    <p class="system-note">Sampled every 15 seconds and kept for 24 hours, including after a restart. Memory and processor are this server. Load is the machine. Network leaves out loopback.</p>

    <p v-if="error && !stats" class="system-error">{{ error }}</p>
    <p v-else-if="!stats" class="system-muted">loading system statistics…</p>
    <p v-else-if="!current" class="system-muted">Collecting the first sample.</p>

    <template v-else>
      <div class="metric-grid">
        <div class="metric-card">
          <div class="metric-value">{{ memoryValue }}</div>
          <div class="metric-label">memory</div>
          <div class="metric-meta">{{ memoryMeta }}</div>
          <div class="metric-meta">{{ processMeta }}</div>
        </div>
        <div class="metric-card">
          <div class="metric-value">{{ formatPercent(current.cpu.system) }}</div>
          <div class="metric-label">cpu</div>
          <div class="metric-meta">{{ cpuMeta }}</div>
        </div>
        <div class="metric-card">
          <div class="metric-value">{{ formatLoad(current.load.one) }}</div>
          <div class="metric-label">system load</div>
          <div class="metric-meta">5 min {{ formatLoad(current.load.five) }} · 15 min {{ formatLoad(current.load.fifteen) }}</div>
        </div>
        <div class="metric-card">
          <div class="metric-value">{{ formatRate(current.network.rx) }}</div>
          <div class="metric-label">network in</div>
          <div class="metric-meta">{{ formatRate(current.network.tx) }} out</div>
        </div>
        <div class="metric-card">
          <div class="metric-value">{{ diskValue }}</div>
          <div class="metric-label">disk</div>
          <div class="metric-meta">{{ diskMeta }}</div>
        </div>
      </div>

      <p class="fact-row">
        <span>process uptime {{ formatDuration(current.process.uptime) }}</span>
        <span>system uptime {{ formatDuration(current.systemUptime) }}</span>
        <span v-if="current.eventLoopMs != null">event loop {{ formatLoop(current.eventLoopMs) }}</span>
        <span v-if="current.process.files != null">open files {{ current.process.files }}</span>
      </p>

      <div class="chart-grid">
        <article v-for="chart in charts" :key="chart.id" class="chart-card" @mouseleave="hover = null">
          <h4 class="chart-title">{{ chart.title }}</h4>
          <p v-if="!chart.ready" class="chart-empty">Waiting for the next sample.</p>
          <div v-else class="chart-frame">
            <div class="y-axis" aria-hidden="true">
              <span
                v-for="(tick, index) in chart.yTicks"
                :key="`${chart.id}-y-${index}`"
                class="y-tick"
                :style="{ bottom: `${tick.percent}%` }"
              >{{ tick.label }}</span>
            </div>
            <div class="plot-column">
          <svg
            class="chart-svg"
            viewBox="0 0 600 112"
            preserveAspectRatio="none"
            role="img"
            :aria-label="chart.label"
            @mousemove="move(chart, $event)"
          >
            <line
              v-for="(tick, index) in chart.yTicks"
              :key="`${chart.id}-yg-${index}`"
              x1="0"
              :y1="tick.y"
              x2="600"
              :y2="tick.y"
              class="chart-rule"
              vector-effect="non-scaling-stroke"
            />
            <line
              v-for="(tick, index) in chart.xTicks"
              :key="`${chart.id}-xg-${index}`"
              :x1="tick.x"
              y1="0"
              :x2="tick.x"
              y2="112"
              class="chart-rule"
              vector-effect="non-scaling-stroke"
            />
            <template v-for="(series, seriesIndex) in chart.drawn" :key="`${chart.id}-${seriesIndex}`">
              <path
                v-for="(segment, segmentIndex) in series"
                :key="`${chart.id}-area-${seriesIndex}-${segmentIndex}`"
                :d="segment.area"
                :fill="chart.colors[seriesIndex]"
                fill-opacity="0.16"
                stroke="none"
              />
              <path
                v-for="(segment, segmentIndex) in series"
                :key="`${chart.id}-line-${seriesIndex}-${segmentIndex}`"
                :d="segment.line"
                fill="none"
                :stroke="chart.colors[seriesIndex]"
                stroke-width="1.6"
                stroke-linejoin="round"
                stroke-linecap="round"
                vector-effect="non-scaling-stroke"
              />
            </template>
            <line
              v-if="hover && hover.id === chart.id"
              :x1="hover.x"
              y1="0"
              :x2="hover.x"
              y2="112"
              class="chart-cursor"
              vector-effect="non-scaling-stroke"
            />
          </svg>
          <div class="x-axis" aria-hidden="true">
            <span
              v-for="(tick, index) in chart.xTicks"
              :key="`${chart.id}-x-${index}`"
              class="x-tick"
              :class="`x-tick-${tick.align}`"
              :style="{ left: `${tick.percent}%` }"
            >{{ tick.label }}</span>
          </div>
          <div v-if="hover && hover.id === chart.id" class="chart-tip" :style="tipStyle">
            <div class="chart-tip-time">{{ hover.time }}</div>
            <div v-for="row in hover.rows" :key="row.name" class="chart-tip-row">
              <i class="legend-swatch" :style="{ background: row.color }"></i>{{ row.name }} {{ row.text }}
            </div>
          </div>
            </div>
          </div>
          <div class="chart-legend">
            <span v-for="series in chart.series" :key="series.name" class="legend-item">
              <i class="legend-swatch" :style="{ background: series.color }"></i>{{ series.name }}
            </span>
          </div>
        </article>
      </div>
      <p v-if="error" class="system-error">{{ error }}</p>
    </template>
  </section>
</template>

<script>

  import axios from 'axios'

  const PLOT_W = 600
  const PLOT_H = 112

  export default {
    data () {

      return {
        stats: null,
        error: ``,
        range: `1h`,
        hover: null,
        requestId: 0,
        timer: null,
        rangeChoices: [
          { id: `15m`, label: `15 min` },
          { id: `1h`, label: `1 hour` },
          { id: `6h`, label: `6 hours` },
          { id: `24h`, label: `24 hours` }
        ]
      }
    },
    computed: {
      current () {

        return this.stats && this.stats.now
      },
      memoryValue () {

        if (!this.current) return `n/a`

        if (this.current.memory.percent != null) return formatPercent(this.current.memory.percent)

        return formatBytes(this.current.memory.used)
      },
      memoryMeta () {

        if (!this.current) return ``

        const used = formatBytes(this.current.memory.used)
        const limit = this.current.memory.limit

        if (limit) return `${used} of ${formatBytes(limit)}`

        return used
      },
      processMeta () {

        if (!this.current) return ``

        return `process ${formatBytes(this.current.process.rss)} · heap ${formatBytes(this.current.process.heapUsed)}`
      },
      diskValue () {

        if (!this.current || !this.current.disk) return `n/a`

        if (this.current.disk.percent != null) return formatPercent(this.current.disk.percent)

        return formatBytes(this.current.disk.used)
      },
      diskMeta () {

        if (!this.current || !this.current.disk) return ``

        return `${formatBytes(this.current.disk.used)} of ${formatBytes(this.current.disk.total)}`
      },
      cpuMeta () {

        if (!this.current) return ``

        const where = this.current.cpu.source === `server` ? `this server` : `this machine`
        const cores = this.current.cpu.cores ? ` · ${this.current.cpu.cores} cores` : ``

        return `${where}${cores} · process ${formatPercent(this.current.cpu.process)}`
      },
      charts () {

        const series = this.stats && this.stats.series
        const at = series && Array.isArray(series.at) ? series.at : []
        const limit = this.current && this.current.memory.limit

        return [
          this.chart(`memory`, `memory`, at, limit, formatBytes, [
            { name: `used`, color: `#9ad12b`, values: series ? series.memory : [] },
            { name: `process`, color: `#8ab4f8`, values: series ? series.rss : [] }
          ]),
          this.chart(`cpu`, `cpu`, at, null, formatPercent, [
            { name: `server`, color: `#9ad12b`, values: series ? series.cpu : [] },
            { name: `process`, color: `#8ab4f8`, values: series ? series.processCpu : [] }
          ]),
          this.chart(`load`, `system load`, at, null, formatLoad, [
            { name: `1 min`, color: `#e0b15a`, values: series ? series.load : [] },
            { name: `5 min`, color: `#8ab4f8`, values: series ? series.load5 : [] }
          ]),
          this.chart(`network`, `network`, at, null, formatRate, [
            { name: `in`, color: `#9ad12b`, values: series ? series.rx : [] },
            { name: `out`, color: `#8ab4f8`, values: series ? series.tx : [] }
          ])
        ]
      },
      tipStyle () {

        if (!this.hover) return {}

        const shift = this.hover.left < 18 ? `0` : this.hover.left > 82 ? `-100%` : `-50%`

        return { left: `${this.hover.left}%`, transform: `translateX(${shift})` }
      }
    },
    mounted () {

      this.load()
      this.timer = setInterval(() => this.load(), 5000)
    },
    beforeUnmount () {

      if (this.timer) clearInterval(this.timer)
    },
    methods: {
      formatBytes,
      formatPercent,
      formatLoad,
      formatRate,
      formatDuration,
      formatLoop,
      setRange (range) {

        if (this.range === range) return

        this.range = range
        this.hover = null
        this.load()
      },
      async load () {

        const range = this.range
        const token = ++this.requestId

        try {

          const response = await axios.get(`${VUE_APP_APP_SERVER_URL}/account/system-stats?range=${range}`, {
            withCredentials: true,
            timeout: 10000
          })

          if (token !== this.requestId) return

          this.stats = response.data || null
          this.error = ``
        } catch (error) {

          if (token !== this.requestId) return
          if (!this.stats) this.error = `could not load system statistics`
        }
      },
      chart (id, title, at, ceiling, format, series) {

        const values = series.reduce((all, item) => all.concat(item.values || []), [])
        const peak = values.reduce((max, value) => Number.isFinite(value) ? Math.max(max, value) : max, 0)
        const scaled = ceiling && ceiling > 0 ? evenScale(Math.max(ceiling, peak), 4) : niceScale(peak)
        const labels = distinctLabels(scaled.ticks, format)
        const ready = values.some((value) => Number.isFinite(value))
        const yTicks = scaled.ticks.map((value, index) => ({
          value,
          label: labels[index],
          percent: (value / scaled.top) * 100,
          y: PLOT_H - (value / scaled.top) * PLOT_H
        }))

        return {
          id,
          title,
          at,
          count: at.length,
          yMax: scaled.top,
          yTicks,
          xTicks: timeTicks(at, this.range),
          ready,
          label: `${title} from ${labels[0]} to ${labels[labels.length - 1]}`,
          colors: series.map((item) => item.color),
          series: series.map((item) => ({ ...item, format })),
          drawn: series.map((item) => drawSeries(item.values || [], at.length, scaled.top))
        }
      },
      move (chart, event) {

        const rect = event.currentTarget.getBoundingClientRect()

        if (!rect.width || chart.count < 1) return

        const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
        const index = chart.count === 1 ? 0 : Math.round(ratio * (chart.count - 1))

        this.hover = {
          id: chart.id,
          x: chart.count === 1 ? PLOT_W / 2 : (index / (chart.count - 1)) * PLOT_W,
          left: ratio * 100,
          time: formatClock(chart.at[index], this.range),
          rows: chart.series.map((series) => ({
            name: series.name,
            color: series.color,
            text: series.format(series.values[index])
          }))
        }
      }
    }
  }

  function evenScale (top, divisions) {

    const ticks = []

    for (let index = 0; index <= divisions; index += 1) ticks.push((top / divisions) * index)

    return { top, ticks }
  }

  function niceScale (peak) {

    if (!(peak > 0)) return evenScale(1, 4)

    const rough = peak / 4
    const pow = Math.pow(10, Math.floor(Math.log10(rough)))
    const fraction = rough / pow
    const step = (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10) * pow
    const top = Math.ceil(peak / step - 1e-9) * step
    const ticks = []

    for (let value = 0; value < top - step * 0.5; value += step) ticks.push(value)

    ticks.push(top)

    return { top, ticks }
  }

  function distinctLabels (ticks, format) {

    const labels = ticks.map((value) => format(value))

    if (new Set(labels).size === ticks.length) return labels

    const step = ticks.length > 1 ? Math.abs(ticks[1] - ticks[0]) : ticks[0] || 1
    const digits = Math.min(4, Math.max(0, Math.ceil(-Math.log10(step)) + 1))

    return ticks.map((value) => {

      if (format === formatPercent) return `${value.toFixed(digits)}%`
      if (format === formatLoad) return value.toFixed(Math.max(2, digits))
      if (format === formatRate) return `${formatBytes(value, digits)}/s`

      return formatBytes(value, digits)
    })
  }

  function timeTicks (at, range) {

    if (!at.length) return []

    const slots = at.length === 1 ? [0.5] : at.length === 2 ? [0, 1] : [0, 0.5, 1]

    return slots.map((slot) => {

      const index = at.length === 1 ? 0 : Math.round(slot * (at.length - 1))
      const percent = at.length === 1 ? 50 : (index / (at.length - 1)) * 100

      return {
        at: at[index],
        percent,
        x: (percent / 100) * PLOT_W,
        label: formatClock(at[index], range),
        align: percent <= 2 ? `start` : percent >= 98 ? `end` : `center`
      }
    })
  }

  function drawSeries (values, count, yMax) {

    const segments = []
    let current = []

    values.forEach((value, index) => {

      if (!Number.isFinite(value)) {

        if (current.length) segments.push(current)
        current = []

        return
      }

      const x = count <= 1 ? PLOT_W / 2 : (index / (count - 1)) * PLOT_W
      const y = PLOT_H - (Math.min(Math.max(value, 0), yMax) / yMax) * PLOT_H

      current.push({ x, y })
    })

    if (current.length) segments.push(current)

    return segments.map((segment) => {

      const line = segment.map((point, index) => `${index ? `L` : `M`}${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join(` `)
      const first = segment[0]
      const last = segment[segment.length - 1]

      return {
        line,
        area: `${line} L${last.x.toFixed(2)} ${PLOT_H} L${first.x.toFixed(2)} ${PLOT_H} Z`
      }
    })
  }

  function formatBytes (value, digits) {

    if (!Number.isFinite(value)) return `n/a`

    const units = [`B`, `KB`, `MB`, `GB`, `TB`]
    let size = Math.max(0, value)
    let unit = 0

    while (size >= 1024 && unit < units.length - 1) {

      size /= 1024
      unit += 1
    }

    const places = digits == null ? (Number.isInteger(size) || size >= 100 ? 0 : size >= 10 ? 1 : 2) : digits

    return `${size.toFixed(places)} ${units[unit]}`
  }

  function formatPercent (value) {

    if (!Number.isFinite(value)) return `n/a`

    return `${value.toFixed(value >= 10 ? 0 : 1)}%`
  }

  function formatLoad (value) {

    if (!Number.isFinite(value)) return `n/a`

    return value.toFixed(2)
  }

  function formatRate (value) {

    if (!Number.isFinite(value)) return `n/a`

    return `${formatBytes(value)}/s`
  }

  function formatLoop (value) {

    if (!Number.isFinite(value)) return `n/a`
    if (value >= 10) return `${value.toFixed(0)} ms`

    return `${value.toFixed(1)} ms`
  }

  function formatDuration (seconds) {

    if (!Number.isFinite(seconds) || seconds < 0) return `n/a`

    const whole = Math.floor(seconds)
    const days = Math.floor(whole / 86400)
    const hours = Math.floor((whole % 86400) / 3600)
    const minutes = Math.floor((whole % 3600) / 60)

    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${minutes}m`
    if (minutes > 0) return `${minutes}m`

    return `${whole}s`
  }

  function formatClock (value, range) {

    const date = new Date(value)

    if (!Number.isFinite(value) || Number.isNaN(date.getTime())) return ``

    if (range === `6h` || range === `24h`) {

      return date.toLocaleString(undefined, { month: `short`, day: `numeric`, hour: `numeric`, minute: `2-digit` })
    }

    return date.toLocaleTimeString(undefined, { hour: `numeric`, minute: `2-digit` })
  }

</script>

<style scoped>

.system-stats {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.system-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.system-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #e8e8e6;
}

.system-note,
.system-muted,
.fact-row {
  margin: 0;
  color: #707070;
  font-size: 0.78rem;
}

.system-error {
  margin: 0;
  color: #e05252;
  font-size: 0.78rem;
}

.range-row {
  display: flex;
  gap: 6px;
}

.range-button {
  background: transparent;
  color: #707070;
  border: 1px solid #2a2a27;
  border-radius: 999px;
  font: inherit;
  font-size: 0.72rem;
  padding: 4px 10px;
  cursor: pointer;
}

.range-button:hover,
.range-button-active {
  color: #e8e8e6;
  border-color: #9ad12b;
}

.metric-grid,
.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.metric-grid {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.metric-card,
.chart-card {
  position: relative;
  background-color: #141412;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  padding: 14px 16px 12px;
}

.metric-value {
  font-size: 1.45rem;
  font-weight: 600;
  color: #e8e8e6;
  line-height: 1.2;
}

.metric-label,
.chart-title {
  margin: 6px 0 0;
  color: #8a8a86;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.metric-meta {
  margin-top: 4px;
  color: #707070;
  font-size: 0.75rem;
}

.fact-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 0;
}

.fact-row span + span::before {
  content: '·';
  margin: 0 10px;
  color: #3a3a37;
}

.chart-title {
  margin: 0;
  font-size: 0.8rem;
  color: #e8e8e6;
}

.chart-empty {
  color: #707070;
  font-size: 0.72rem;
  margin: 28px 0;
}

.chart-frame {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  column-gap: 8px;
  margin-top: 10px;
}

.y-axis {
  position: relative;
  height: 132px;
}

.y-tick {
  position: absolute;
  right: 0;
  color: #8a8a86;
  font-size: 0.68rem;
  line-height: 1;
  white-space: nowrap;
  transform: translateY(50%);
}

.plot-column {
  position: relative;
  min-width: 0;
}

.chart-svg {
  display: block;
  width: 100%;
  height: 132px;
}

.x-axis {
  position: relative;
  height: 16px;
  margin-top: 4px;
}

.x-tick {
  position: absolute;
  top: 0;
  color: #8a8a86;
  font-size: 0.68rem;
  line-height: 1;
  white-space: nowrap;
}

.x-tick-start {
  transform: none;
}

.x-tick-center {
  transform: translateX(-50%);
}

.x-tick-end {
  transform: translateX(-100%);
}

.chart-rule,
.chart-cursor {
  stroke: #2e2e2a;
}

.chart-cursor {
  stroke: #e8e8e6;
  stroke-dasharray: 2 3;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-top: 6px;
}

.legend-item,
.chart-domain {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #707070;
  font-size: 0.72rem;
}

.chart-domain {
  margin-left: auto;
}

.legend-swatch {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 99px;
}

.chart-tip {
  position: absolute;
  top: 36px;
  z-index: 2;
  min-width: 140px;
  padding: 8px 10px;
  background: #0f0f0d;
  border: 1px solid #2a2a27;
  border-radius: 6px;
  pointer-events: none;
}

.chart-tip-time,
.chart-tip-row {
  color: #e8e8e6;
  font-size: 0.72rem;
}

.chart-tip-time {
  margin-bottom: 4px;
  color: #9ad12b;
}

@media (max-width: 860px) {
  .metric-grid,
  .chart-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .system-heading,
  .metric-grid,
  .chart-grid {
    grid-template-columns: 1fr;
  }

  .system-heading {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
}

</style>
